import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Card, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { ChangeHighlighter } from '@rd-web-markets/shared/dist/util/ChangeHighlighter';
import { Loading } from '@rd-web-markets/shared/dist/util';
import ClaimStateAlerts from '@rd-web-markets/shared/dist/claim/ClaimStateAlerts';
import { apiKey, getDefaultConfig } from '@rd-web-markets/shared/dist/util/tinyMceConfig';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { createTimezonedDate } from '@rd-web-markets/shared/dist/util/date/dateUtils';
import paymentsToClinicalTrialsService from '../../services/claim/payments_to_clinical_trials.service';
import { PaymentsToClinicalTrialsHeader, PaymentsToClinicalTrialsRow, PaymentsToClinicalTrialsTotalsRow } from '.';
import { AddButton } from '@rd-web-markets/shared/dist/util/buttons';
import SectionsFooter from '../SectionsFooter';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import NotesList from '@rd-web-markets/shared/dist/financialSection/NotesList';
import CostFragmentsTable from '../costFragments/CostFragmentsTable';
import AddNoteButton from '../AddNoteButton';
import ReactDataGridCostFragmentsTable from '../costFragments/ReactDataGridCostFragmentsTable';
export const PaymentsToClinicalTrialsSection = _ref => {
  let {
    accountType,
    claim,
    refreshClaim,
    showCostFragmentsTable,
    debouncedSaveClaim,
    setClaim,
    totalPayrollCostInvalid,
    allProjects
  } = _ref;
  const [paymentsToClinicalTrials, setPaymentsToClinicalTrials] = useState(null);
  const [headerScrollPosition, setHeaderScrollPosition] = useState(0);
  const dispatch = useDispatch();
  const claimId = useMemo(() => claim?.id, [claim]);
  const user = useSelector(state => state.auth.user);
  const [isShowInvolvement, setIsShowInvolvement] = useState(null);
  const getData = useErrorHandling(useCallback(async () => {
    setPaymentsToClinicalTrials(await paymentsToClinicalTrialsService.all(claimId));
  }, [claimId]));
  useEffect(() => {
    getData();
    setIsShowInvolvement(claim.claim_group.cost_methodology === 'consolidated');
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const paymentsToClinicalTrial = paymentsToClinicalTrials[index];
    if (paymentsToClinicalTrial.id) {
      try {
        await paymentsToClinicalTrialsService.delete(paymentsToClinicalTrial);
        const paymentsToClinicalTrials = await paymentsToClinicalTrialsService.all(claim.id);
        setPaymentsToClinicalTrials(paymentsToClinicalTrials);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newPaymentsToClinicalTrials = [...paymentsToClinicalTrials];
      newPaymentsToClinicalTrials.splice(index, 1);
      setPaymentsToClinicalTrials(newPaymentsToClinicalTrials);
    }
  };
  const handleAdd = paymentsToClinicalTrial => {
    setPaymentsToClinicalTrials([...paymentsToClinicalTrials, paymentsToClinicalTrial]);
  };
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  const userUnauthorizedToEdit = !hasPermissions();
  const sortedClaimProjectReports = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.sort((a, b) => {
      if (a.project_name < b.project_name) {
        return -1;
      }
      if (a.project_name > b.project_name) {
        return 1;
      }
      return 0;
    });
  }, [claim]);
  if (!claim || !paymentsToClinicalTrials) return /*#__PURE__*/React.createElement(Loading, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClaimStateAlerts, {
    claim: claim
  }), /*#__PURE__*/React.createElement(Alert, {
    variant: "info"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "info"), " Please ensure that:", /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", null, "all costs were incurred between ", createTimezonedDate(claim.from_date).toCustomLocaleDateString(), " and ", createTimezonedDate(claim.to_date).toCustomLocaleDateString()), /*#__PURE__*/React.createElement("li", null, "total Utilities cost amount matches the financial accounts P&L"))), totalPayrollCostInvalid ? /*#__PURE__*/React.createElement(Alert, {
    variant: "warning"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "warning"), " Total payroll cost is either empty or smaller than the sum of employee costs. Please go to the Staffing Costs tab to correct this.") : null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement(AddNoteButton, {
    claimId: claim.id,
    category: "payments_to_clinical_trials",
    userCanEdit: hasPermissions() && !claim.financials_locked && !claim.is_locked
  })), !showCostFragmentsTable && /*#__PURE__*/React.createElement(React.Fragment, null, paymentsToClinicalTrials.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(PaymentsToClinicalTrialsHeader, {
    claim: claim,
    setHeaderScrollPosition: setHeaderScrollPosition,
    isShowInvolvement: isShowInvolvement,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement("tbody", null, paymentsToClinicalTrials.map((paymentsToClinicalTrial, index) => {
    return /*#__PURE__*/React.createElement(PaymentsToClinicalTrialsRow, {
      key: index,
      index: index,
      claim: claim,
      sortedClaimProjectReports: sortedClaimProjectReports,
      headerScrollPosition: headerScrollPosition,
      handleDelete: () => handleDelete(index),
      paymentsToClinicalTrials: paymentsToClinicalTrials,
      setPaymentsToClinicalTrials: setPaymentsToClinicalTrials,
      refreshClaim: refreshClaim,
      totalPayrollCostInvalid: totalPayrollCostInvalid,
      handleError: handleError,
      paymentsToClinicalTrialsService: paymentsToClinicalTrialsService,
      isShowInvolvement: isShowInvolvement
    });
  }), /*#__PURE__*/React.createElement(PaymentsToClinicalTrialsTotalsRow, {
    claim: claim,
    isShowInvolvement: isShowInvolvement
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), hasPermissions() && /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(AddButton, {
    text: "Add Row",
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      claim_employee_rnd_apportionments: []
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  })))), showCostFragmentsTable && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::PaymentsToClinicalTrial",
    userUnauthorizedToEdit: userUnauthorizedToEdit,
    allProjects: allProjects
  }), /*#__PURE__*/React.createElement(SectionsFooter, {
    claim: claim,
    accountType: accountType,
    backLink: `/${accountType}/claims/${claim.id}/financial_details/data_licences_costs`
  }));
};